import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Backlog } from '../util-cmps/backlog'
import { useDraggable } from '../../hooks/use-draggable'
import { useEscapeKey } from '../../hooks/use-escape-key'
import { useScrollLock } from '../../hooks/use-scroll-lock'
import { newHttpService } from '../../services/new-http-service'
import { utilService } from '../../services/util-service'

interface HistoricalWorkingPaperRecord {
  id: number
  audit_control_id: number
  control_result_id: number
  created_at: string | null
  updated_at: string | null
  date_reviewed: string | null
  expected_effectiveness: string | null
  reviewed_by_email: string | null
  reviewed_by_name: string | null
  audit_year: number | null
}

interface HistoricalWorkingPaperModalProps {
  isOpen: boolean
  auditControlId: string
  companyName?: string
  initialDate: string
  onClose: () => void
  onDownload: (controlResultId: number) => Promise<boolean>
}

const toDateInputValue = (value: string): string => {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  }
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`
}

const formatDate = (value: string | null): string => value ? utilService.formatDateTime(value) : '—'

export const parseDateOnly = (value: string | null): number | null => {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
}

export const getPreferredControlResultId = (
  records: HistoricalWorkingPaperRecord[],
  requestedSelectedDate: string,
  approvedOnly: boolean,
): number | null => {
  if (approvedOnly) {
    return records[0]?.control_result_id ?? null
  }

  const targetDay = parseDateOnly(requestedSelectedDate)
  if (targetDay == null) {
    return records[0]?.control_result_id ?? null
  }

  const exactMatch = records.find(record => parseDateOnly(record.created_at) === targetDay)
  if (exactMatch) return exactMatch.control_result_id

  const nearestMatch = records
    .map(record => {
      const recordDay = parseDateOnly(record.created_at)
      return recordDay == null ? null : {
        record,
        distance: Math.abs(recordDay - targetDay),
      }
    })
    .filter((item): item is { record: HistoricalWorkingPaperRecord, distance: number } => item !== null)
    .sort((a, b) => a.distance - b.distance)[0]

  return nearestMatch?.record.control_result_id ?? records[0]?.control_result_id ?? null
}

export const getVisibleHistoricalWorkingPaperRecords = (
  records: HistoricalWorkingPaperRecord[],
  approvedOnly: boolean,
): HistoricalWorkingPaperRecord[] =>
  approvedOnly
    ? records.filter(record => (record.expected_effectiveness || '').trim().toLowerCase() === 'effective')
    : records

const HistoricalWorkingPaperModal: React.FC<HistoricalWorkingPaperModalProps> = ({
  isOpen,
  auditControlId,
  companyName,
  initialDate,
  onClose,
  onDownload,
}) => {
  useEscapeKey(onClose)
  useScrollLock()
  const { modalRef, onHeaderMouseDown } = useDraggable<HTMLElement>()
  const dateInputRef = React.useRef<HTMLInputElement | null>(null)
  const itemRefs = React.useRef(new Map<number, HTMLButtonElement | null>())

  const [selectedDate, setSelectedDate] = useState(() => toDateInputValue(initialDate))
  const [records, setRecords] = useState<HistoricalWorkingPaperRecord[]>([])
  const [selectedControlResultId, setSelectedControlResultId] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [approvedOnly, setApprovedOnly] = useState(false)
  const requestSelectedDate = useMemo(
    () => (approvedOnly ? toDateInputValue(initialDate) : selectedDate),
    [approvedOnly, initialDate, selectedDate]
  )

  useEffect(() => {
    if (!isOpen) return
    setSelectedDate(toDateInputValue(initialDate))
    setSelectedControlResultId(null)
    setError(null)
    setRecords([])
    setApprovedOnly(false)
  }, [isOpen, initialDate])

  useEffect(() => {
    if (!isOpen) return
    const id = window.setTimeout(() => {
      dateInputRef.current?.focus()
    }, 0)
    return () => window.clearTimeout(id)
  }, [isOpen, initialDate])

  useEffect(() => {
    if (!isOpen) return
    let active = true
    setIsLoading(true)
    setError(null)
    const params: Record<string, string> = {
      audit_control_id: auditControlId,
      selected_date: requestSelectedDate,
      ...(companyName ? { company_name: companyName } : {}),
    }
    if (approvedOnly) {
      params.approved_only = 'true'
    }
    newHttpService.get('/api/backoffice/working-paper-history', params)
      .then((res: HistoricalWorkingPaperRecord[] | any) => {
        if (!active) return
        const nextRecords = Array.isArray(res) ? res : []
        setRecords(nextRecords)
        setSelectedControlResultId(() => getPreferredControlResultId(nextRecords, requestSelectedDate, approvedOnly))
      })
      .catch((err) => {
        if (!active) return
        setRecords([])
        setSelectedControlResultId(null)
        setError(err?.response?.data?.detail || 'Failed to load historical Working Paper sessions.')
      })
      .finally(() => {
        if (!active) return
        setIsLoading(false)
      })
    return () => { active = false }
  }, [isOpen, auditControlId, companyName, requestSelectedDate, approvedOnly])

  const selectedRecord = useMemo(
    () => {
      const filtered = getVisibleHistoricalWorkingPaperRecords(records, approvedOnly)
      return filtered.find(record => record.control_result_id === selectedControlResultId) || null
    },
    [records, selectedControlResultId, approvedOnly]
  )

  const visibleRecords = useMemo(
    () => getVisibleHistoricalWorkingPaperRecords(records, approvedOnly),
    [records, approvedOnly]
  )

  useEffect(() => {
    if (!isOpen) return
    setSelectedControlResultId(prev => {
      if (prev && visibleRecords.some(record => record.control_result_id === prev)) return prev
      return visibleRecords[0]?.control_result_id ?? null
    })
  }, [isOpen, visibleRecords])

  useEffect(() => {
    if (!isOpen || selectedControlResultId == null) return
    const node = itemRefs.current.get(selectedControlResultId)
    if (!node) return
    requestAnimationFrame(() => {
      node.scrollIntoView({ block: 'center', inline: 'nearest' })
    })
  }, [isOpen, selectedControlResultId, visibleRecords])

  const handleDownload = async () => {
    if (!selectedRecord) return
    setIsDownloading(true)
    try {
      const started = await onDownload(selectedRecord.control_result_id)
      if (started) onClose()
    } finally {
      setIsDownloading(false)
    }
  }

  if (!isOpen) return null

  return createPortal(
    <>
      <section ref={modalRef} className="form-modal wp-history-modal" style={{ zIndex: 320 }} role="dialog" aria-modal="true" aria-label="Historical Working Paper Download">
        <header onMouseDown={onHeaderMouseDown}>
          <h2>Historical Working Paper Download</h2>
          <button className="close-btn" onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </header>

        <section className="input-section wp-history-body">
          <div className="form-section-card wp-history-card">
            <div className="wp-history-controls">
              <div className="wp-history-mode-toggle" role="tablist" aria-label="Historical Working Paper filter mode">
                <button
                  type="button"
                  className={!approvedOnly ? 'active' : ''}
                  onClick={() => setApprovedOnly(false)}
                  aria-pressed={!approvedOnly}
                >
                  By Date
                </button>
                <button
                  type="button"
                  className={approvedOnly ? 'active' : ''}
                  onClick={() => setApprovedOnly(true)}
                  aria-pressed={approvedOnly}
                >
                  Approved Working Papers Only
                </button>
              </div>
              <div className={`wp-history-mode-group${approvedOnly ? ' wp-history-mode-group--approved' : ''}`}>
                {!approvedOnly ? (
                  <label className="field wp-history-date-field">
                    <span>Choose a date</span>
                    <input
                      ref={dateInputRef}
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                    />
                  </label>
                ) : (
                  <div className="field-hint wp-history-hint">
                    Approved mode ignores the date filter and shows the reviewed working papers only.
                  </div>
                )}
              </div>
              {!approvedOnly && (
                <div className="field-hint wp-history-hint">
                  Shows control results within the audit year and about one month around the selected day.
                </div>
              )}
            </div>
          </div>

          <div className="form-section-card wp-history-results-card">
            {error ? (
              <div className="partial-warning wp-history-state wp-history-state--error">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e65100" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{error}</span>
              </div>
            ) : isLoading ? (
              <div className="wp-history-state">Loading control results...</div>
            ) : visibleRecords.length === 0 ? (
              <div className="wp-history-state">
                {approvedOnly
                  ? 'No approved working papers found.'
                  : 'No control results found for this date window.'}
              </div>
            ) : (
              <>
                <div className="wp-history-list-label">Matching sessions</div>
                <div className="wp-history-list">
                  {visibleRecords.map(record => {
                    const isSelected = record.control_result_id === selectedControlResultId
                    const effectiveness = (record.expected_effectiveness || '').trim()
                    const isApproved = effectiveness.toLowerCase() === 'effective'
                    return (
                    <button
                      key={record.control_result_id}
                      ref={(node) => { itemRefs.current.set(record.control_result_id, node) }}
                      type="button"
                      className={`wp-history-item${isSelected ? ' wp-history-item--selected' : ''}`}
                      onClick={() => setSelectedControlResultId(record.control_result_id)}
                    >
                      <div className="wp-history-item-main">
                        <span className="wp-history-item-title">Control Result #{record.control_result_id}</span>
                        <span className="wp-history-item-date">{formatDate(record.created_at)}</span>
                      </div>
                      <div className="wp-history-item-meta">
                        {approvedOnly ? (
                          <>
                            <span className="wp-history-item-cell">
                              <span className="wp-history-item-cell-label">Reviewed By</span>
                              <span className="wp-history-item-cell-value">{record.reviewed_by_name || record.reviewed_by_email || '—'}</span>
                            </span>
                            <span className="wp-history-item-cell">
                              <span className="wp-history-item-cell-label">Date Reviewed</span>
                              <span className="wp-history-item-cell-value">{formatDate(record.date_reviewed)}</span>
                            </span>
                            <span className="wp-history-item-cell">
                              <span className="wp-history-item-cell-label">Effectiveness</span>
                              <span className={`wp-history-effectiveness${isApproved ? ' wp-history-effectiveness--approved' : ''}`}>{effectiveness || '—'}</span>
                            </span>
                          </>
                        ) : (
                          <>
                            <span>{record.expected_effectiveness || 'Unknown effectiveness'}</span>
                            <span>{record.reviewed_by_name || record.reviewed_by_email || 'Unreviewed'}</span>
                          </>
                        )}
                      </div>
                    </button>
                  )
                  })}
                </div>
              </>
            )}
          </div>

          <div className="wp-history-footer">
            <button className="main wp-history-download" onClick={handleDownload} disabled={!selectedRecord || isDownloading || isLoading}>
              {isDownloading ? 'Starting download...' : 'Download WP'}
            </button>
          </div>
        </section>
      </section>
      <Backlog zIndex={319} onCloseBacklog={onClose} />
    </>,
    document.body
  )
}

export default HistoricalWorkingPaperModal
